import { Modal, Text } from '@mantine/core'
import { useState, type ReactNode } from 'react'

import { FormActions } from './FormActions'

/**
 * Asks before a destructive act (UX spec §4.5): "Delete Push A?", one sentence of what follows, Cancel and Delete.
 * Kept to Mantine's Modal rather than @mantine/modals so there is no new package.
 */
export function ConfirmModal(props: {
  opened: boolean
  title: string
  body: ReactNode
  confirmLabel?: string
  /** For a question that isn't quite Cancel/Delete, such as "Keep my edits" (setup's "Your training" step). */
  cancelLabel?: string
  loading?: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <Modal opened={props.opened} onClose={props.onCancel} title={props.title} size="sm">
      <Text size="sm">{props.body}</Text>
      <FormActions
        mt="lg"
        onCancel={props.onCancel}
        cancelLabel={props.cancelLabel}
        onSubmit={props.onConfirm}
        loading={props.loading}
        submitLabel={props.confirmLabel ?? 'Delete'}
        submitColor="red"
        submitAutoFocus
      />
    </Modal>
  )
}

/**
 * The confirm dialog as a hook: `ask({ title, body, onConfirm })` opens it, `modal` renders it. One per page is
 * enough, since only one question is ever open.
 */
export function useConfirm() {
  const [request, setRequest] = useState<{ title: string; body: ReactNode; confirmLabel?: string; cancelLabel?: string; onConfirm: () => void } | null>(null)
  const modal = (
    <ConfirmModal
      opened={request !== null}
      title={request?.title ?? ''}
      body={request?.body ?? ''}
      confirmLabel={request?.confirmLabel}
      cancelLabel={request?.cancelLabel}
      onCancel={() => setRequest(null)}
      onConfirm={() => {
        request?.onConfirm()
        setRequest(null)
      }}
    />
  )
  return { ask: setRequest, modal }
}
